import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ProctoringAddAssessmentDialogComponent } from '../proctoring-add-assessment-dialog/proctoring-add-assessment-dialog.component';
import { PROCTORED_STATUS_OPTIONS, RULE_SETS } from '@edtech-ui/core/constants/recuritment-constants/proctoring-constants/ruleSets';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CreateProctoringAssessmentPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/create-proctoring-assessment';
import { catchError, debounceTime, distinctUntilChanged, finalize, of, Subject, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ProctoringService } from '@edtech-ui/core/services/proctoring-dashboard/proctoring.service';
import { GetProctoringPolicyBindingsPagedPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/get-proctoring-assessment';
import { ProctoringDeleteConfigComponent } from '../proctoring-delete-config/proctoring-delete-config.component';
import { UpdateProctoringAssessmentPayload } from '@edtech-ui/core/models/proctoring-dashboard-models/update-proctoring-assessment';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

@Component({
	selector: 'app-proctoring-assessment',
	standalone: true,
	imports: [
		CommonModule, MatProgressSpinnerModule,
		FormsModule, MatTooltipModule,
		HasPermissionDirective
	],
	templateUrl: './proctoring-assessment.component.html',
	styleUrl: './proctoring-assessment.component.scss'
})
export class ProctoringAssessmentComponent implements OnInit, OnDestroy {
	searchText: string = '';
	showColumnDropdown = false;
	currentPage = 1;
	totalPages = 0
	fromDate: string = '';
	toDate: string = '';
	showIntegrityPopup = false;
	minIntegrity: number | null = null;
	maxIntegrity: number | null = null;
	allRuleSets: string = 'All Rulesets';
	selectedStatus: string = 'All Statuses';
	assessments = RULE_SETS;
	statuses = PROCTORED_STATUS_OPTIONS;
	subscription = new Subscription();
	isLoading = false;
	isInitialLoad = true;
	assessmentData: any[] = [];
	total = 0;
	pageSize = 10;
	sortField = '';
	sortOrder: 'ASC' | 'DESC' = 'DESC';
	private searchSubject = new Subject<string>();
	companyId = '';
	PermissionForStudents = PermissionForStudents;


	constructor(
		private dialog: MatDialog,
		private router: Router,
		private toastr: ToastrService,
		private proctoringService: ProctoringService
	) { }


	ngOnInit(): void {
		this.companyId = localStorage.getItem('company_id') || '';
		this.searchSubject.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
			this.currentPage = 1;
			this.getProctoringAssessment();
		});

		this.getProctoringAssessment();
	}


	toggleIntegrityPopup(): void {
		this.showIntegrityPopup = !this.showIntegrityPopup;
	}


	applyIntegrityFilter() {
		this.showIntegrityPopup = false;
		this.applyFilters();
	}


	resetIntegrityFilter(): void {
		this.minIntegrity = null;
		this.maxIntegrity = null;
	}


	@HostListener('document:click', ['$event'])
	clickOutside(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (!target.closest('.column-filter')) {
			this.showColumnDropdown = false;
		}
	}


	openAddAssessmentDialogBox() {
		const dialogRef = this.dialog.open(ProctoringAddAssessmentDialogComponent, {
			panelClass: 'overflow-visible-dialog',
			data: {
				isEdit: false
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.createAssessment(result.payload);
		});
	}


	createAssessment(payload: CreateProctoringAssessmentPayload) {
		this.subscription.add(
			this.proctoringService.postProctoringAssessment(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.toastr.success('Assessment created successfully');
						this.sortField = '';
						this.sortOrder = 'DESC';
						this.getProctoringAssessment();
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	updateProctoringAssessment(id: string, payload: UpdateProctoringAssessmentPayload) {
		this.subscription.add(
			this.proctoringService.updateProctoringAssessment(payload, id).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success('Assessment updated successfully');
						this.sortField = '';
						this.sortOrder = 'DESC';
						this.getProctoringAssessment();
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	openEditAssessmentDialog(assessment: any) {
		const dialogRef = this.dialog.open(ProctoringAddAssessmentDialogComponent, {
			panelClass: 'overflow-visible-dialog',
			data: {
				isEdit: true,
				assessment
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.updateProctoringAssessment(result.id, result.payload);
		});
	}


	goToAssessmentDetailPage(policyBindingId: string) {
		this.router.navigate(['/proctoring-dashboard/assessment-page', policyBindingId]);
	}


	getProctoringAssessment() {
		this.isLoading = true;

		const payload: GetProctoringPolicyBindingsPagedPayload = {
			page: this.currentPage,
			pageSize: this.pageSize || 10,
			filters: {
				companyId: this.companyId,
				proctoringEvaluationOn: 'ASSESSMENT',
				proctoringEvaluationOnId: '',
				or: []
			},
			sort: [
				{
					field: this.sortField || 'createdAt',
					order: this.sortOrder || 'DESC'
				}
			],
			all: false
		};

		if (this.searchText && this.searchText.trim() !== '') {
			payload.search = this.searchText;
		}
		this.subscription.add(
			this.proctoringService.getProctoringAssessment(payload).pipe(
				tap((response) => {
					if (response.success) {
						this.assessmentData = response.data.data;
						this.total = response.data.pagination.total;
						this.currentPage = response.data.pagination.page;
						this.pageSize = response.data.pagination.pageSize;
						this.totalPages = response.data.pagination.totalPages;
					} else {
						this.toastr.error(response.message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
					this.isInitialLoad = false;
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	get startRecord(): number {
		return this.total === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
	}


	get endRecord(): number {
		return Math.min(this.currentPage * this.pageSize, this.total);
	}


	changePageSize() {
		this.currentPage = 1;
		this.getProctoringAssessment();
	}


	previousPage() {
		if (this.currentPage > 1) {
			this.currentPage--;
			this.getProctoringAssessment();
		}
	}


	nextPage() {
		if (this.currentPage < this.totalPages) {
			this.currentPage++;
			this.getProctoringAssessment();
		}
	}

	goToPage(page: number) {
		if (page >= 1 && page <= this.totalPages) {
			this.currentPage = page;
			this.getProctoringAssessment();
		}
	}

	get pagesArray(): number[] {
		const pages = this.totalPages || Math.ceil(this.total / (this.pageSize || 10)) || 1;
		return Array.from({ length: pages }, (_, i) => i + 1);
	}

	get displayTotalPages(): number {
		return this.totalPages || Math.ceil(this.total / (this.pageSize || 10)) || 1;
	}


	sortBy(field: string) {
		if (this.sortField === field) {
			this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
		} else {
			this.sortField = field;
			this.sortOrder = 'ASC';
		}
		this.currentPage = 1;
		this.getProctoringAssessment();
	}


	applyFilters() {
		this.currentPage = 1;
		this.getProctoringAssessment();
	}


	onSearch() {
		this.searchSubject.next(this.searchText);
	}


	deleteProctoringAssessment(policyBindingId: string) {
		this.subscription.add(
			this.proctoringService.deleteProctoringAssessment(policyBindingId).pipe(
				tap(response => {
					if (response.success) {
						this.toastr.success('Assessment delete successfully');
						this.sortField = '';
						this.sortOrder = 'DESC';
						this.getProctoringAssessment();
					} else {
						this.toastr.error(response.message);
					}
				}),
				catchError(() => {
					return of(null);
				})
			).subscribe()
		);
	}


	openDeleteDialogBox(policyBindingId: string, name: string) {
		const dialogRef = this.dialog.open(ProctoringDeleteConfigComponent, {
			data: {
				name,
				type: 'assessment'
			}
		});

		dialogRef.afterClosed().subscribe(result => {
			if (!result) {
				return;
			}
			this.deleteProctoringAssessment(policyBindingId);
		});
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}